import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { Suspense, useEffect, useState } from 'react';
import packageJson from '../package.json';
import Navbar from './components/navbar';
import NewInvitation from './pages/newInvitation';
import Homepage from './pages/homepage';
import AllInvitations from './pages/allInvitations';
import Invitations from './pages/invitation';
import Login from './pages/login';
import EventsList from './pages/events';
import NewEvent from './pages/events/new';
import EventPlanner from './pages/events/planner';
import SettingsPage from './pages/settings';
import RequireAuth from './components/RequireAuth';
import { useAuth } from './context/AuthContext';
import Footer from './components/footer';
import { Button } from './components/ui/button';
import { TooltipProvider } from './components/ui/tooltip';
import { AdminPageSkeleton, ResponsesPageSkeleton, ResponsesStatsSkeleton } from './components/ui/skeleton';
import ScrollToTop from './components/ScrollToTop';
import OfflineBanner from './components/pwa/OfflineBanner';
import UpdateBanner from './components/pwa/UpdateBanner';
import ChunkLoadErrorBoundary from './components/ChunkLoadErrorBoundary';
import { lazyWithRetry } from './lib/lazyWithRetry';

const Responses = lazyWithRetry(() => import('./pages/responses'));
const ResponseStats = lazyWithRetry(() => import('./pages/responses/stats'));

function RouteSuspenseFallback() {
  const { pathname } = useLocation();
  if (pathname === '/risposte') return <ResponsesPageSkeleton />;
  if (pathname === '/risposte/statistiche') return <ResponsesStatsSkeleton />;
  return <AdminPageSkeleton />;
}

function App() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [packageVersion, setPackageVersion] = useState('');

  useEffect(() => {
    setPackageVersion(packageJson?.version);
  }, []);

  const hideNavbar = location.pathname === '/invito' || location.pathname === '/login';
  const showNavbar = isAuthenticated && !hideNavbar;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex min-h-screen flex-col">
        <ScrollToTop />
        {showNavbar && <Navbar />}
        <OfflineBanner />
        <UpdateBanner />
        <main className="flex-1">
          <ChunkLoadErrorBoundary resetKey={location.pathname}>
            <Suspense fallback={<RouteSuspenseFallback />}>
              <Routes>
                <Route path="/" element={<RequireAuth><Homepage /></RequireAuth>} />
                <Route path="/editor-invito" element={<RequireAuth><NewInvitation /></RequireAuth>} />
                <Route path="/tutti-gli-inviti" element={<RequireAuth><AllInvitations /></RequireAuth>} />
                <Route path="/invito" element={<Invitations />} />
                <Route path="/risposte" element={<RequireAuth><Responses /></RequireAuth>} />
                <Route path="/risposte/statistiche" element={<RequireAuth><ResponseStats /></RequireAuth>} />
                <Route path="/eventi" element={<RequireAuth><EventsList /></RequireAuth>} />
                <Route path="/eventi/nuovo" element={<RequireAuth><NewEvent /></RequireAuth>} />
                <Route path="/eventi/:id" element={<RequireAuth><EventPlanner /></RequireAuth>} />
                <Route path="/impostazioni" element={<RequireAuth><SettingsPage /></RequireAuth>} />
                <Route path="/login" element={<Login />} />
                <Route path="*" element={
                  <RequireAuth>
                    <div className="mx-auto max-w-lg px-4 py-16 text-center">
                      <h1 className="text-2xl font-bold text-brand-danger">404 — Pagina non trovata</h1>
                      <p className="mt-2 ba-muted">La pagina che stai cercando non esiste.</p>
                      <Button asChild className="mt-6">
                        <Link to="/">Torna alla homepage</Link>
                      </Button>
                    </div>
                  </RequireAuth>
                } />
              </Routes>
            </Suspense>
          </ChunkLoadErrorBoundary>
        </main>
        {showNavbar && <Footer packageJson={{ version: packageVersion }} />}
      </div>
    </TooltipProvider>
  );
}

export default App;
